import React, { useEffect } from "react";
// @ts-ignore
import Importer from "@tinyemail/tiny-csv-importer";
import { Dialog, DialogTitle, DialogContent, Box } from "@tiny/components";
import { useTheme } from "@mui/material";
import {
  usePostSegmentImportMutation,
  usePostSegmentCustomersBySegmentIdMutation,
  useGetCustomAttributesQuery,
} from "@tiny/api";
import WarningOutline from "@tiny/icons/generated/WarningOutline";
import useTinyImporter from "./TinyImporter.hooks";
import { TinyImporterProps } from "./TinyImporter.types";
import { closeSnackbar } from "notistack";
import { SegmentIdentityView1 } from "@tiny/api";
import { useTinyHost } from "@tiny/host-contract";
import { LargeFileUploadDialog } from "./LargeFileUploadDialog";

declare global {
  interface Window {
    dataLayer: any;
  }
}

function TinyImporter(props: TinyImporterProps) {
  const { open, onClose, value, segmentDetails, onSuccess } = props;

  const { notify, capabilities } = useTinyHost();
  const muiTheme = useTheme();
  // Derive the CSV importer's palette from the active MUI theme so it matches
  // the host's brand (email = green primary #008060, TinyAlbert = purple). The
  // importer defaults to email green, so this leaves email unchanged.
  const importerTheme = React.useMemo(
    () => ({
      colors: {
        primary: muiTheme.palette.primary.main,
        secondary: muiTheme.palette.primary.main,
        success: muiTheme.palette.primary.main,
        danger: muiTheme.palette.error.main,
        warning: muiTheme.palette.warning.main,
      },
    }),
    [muiTheme]
  );
  const { mutateAsync: postSegmentCustomersMutateAsync } =
    usePostSegmentCustomersBySegmentIdMutation();
  const { mutateAsync: postSegmentMutateAsync } =
    usePostSegmentImportMutation();
  const { data: customAttributesData } = useGetCustomAttributesQuery();
  const customAttributes =
    customAttributesData?.customAttributeDefinitions ?? [];
  const { fields } = useTinyImporter(customAttributes, capabilities.smsEnabled);

  const [largeFileDialogOpen, setLargeFileDialogOpen] = React.useState(false);
  const [largeFile, setLargeFile] = React.useState<File | null>(null);

  const postBulkAdd = async (customers: any) => {
    let targetSegment: SegmentIdentityView1;
    // Get the UTC milliseconds
    const utcMilliseconds = (date: Date) =>
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
      );

    if (customers) {
      const customAttributeNames = customAttributes.map(
        (attr) => attr.attributeName
      );

      const newCustomers = customers?.map((customer: any) => {
        if (customer) {
          if (customer.tags?.length > 0) {
            customer.tags = customer.tags?.split("|");
          } else {
            customer.tags = null;
          }
          if (customer.birthday) {
            customer.birthday = utcMilliseconds(new Date(customer.birthday));
          }
          if (customer.lastOrderDate) {
            customer.lastOrderDate = utcMilliseconds(
              new Date(customer.lastOrderDate)
            );
          }
          // Remap customAttribute_<name> keys to <name>,
          // splitting ARRAY attributes on commas
          customAttributes.forEach((attr) => {
            const attrName = attr.attributeName;
            const prefixedKey = `customAttribute_${attrName}`;
            if (prefixedKey in customer) {
              const rawValue = customer[prefixedKey];
              if (attr.dataType === "ARRAY") {
                customer[attrName] =
                  rawValue?.length > 0
                    ? rawValue
                        .split(",")
                        .map((v: string) => v.trim())
                        .filter(Boolean)
                    : null;
              } else {
                customer[attrName] = rawValue;
              }
              delete customer[prefixedKey];
            }
          });
        }
        return customer;
      });

      try {
        if (segmentDetails?.id) {
          const data = await postSegmentCustomersMutateAsync({
            segmentId: segmentDetails.id,
            data: {
              customers,
              customAttributes: customAttributeNames,
            },
          });
          targetSegment = data;
        } else {
          const segment = await postSegmentMutateAsync({
            data: {
              name: value,
              segmentType: "UPLOAD",
              customers: newCustomers,
              customAttributes: customAttributeNames,
            },
          });
          targetSegment = segment;
        }
        closeSnackbar();
        notify.showUpload?.({
          variant: "success",
          message: "Upload started successfully!",
          description: `Your [${value} ] audience upload is now in progress. please refresh to see the current status`,
          primaryCtaActionText: "Close",
          primaryCtaActionFunction: () => {
            closeSnackbar();
          },
          persist: true,
        });
        onSuccess?.(targetSegment);
      } catch (e) {
        closeSnackbar();
        notify.showUpload?.({
          variant: "failed",
          message: "Uploading error!",
          description:
            "It seems file size is large for browser. Please contact the support team.",
          showClose: true,
        });
        throw new Error();
      }
    }
  };
  const emitGTM = (state: string) => {
    // Guard: hosts without GTM (TinyAlbert) have no window.dataLayer.
    (window.dataLayer = window.dataLayer || []).push({
      event: `tiny-csv-importer-${state}`,
    });
  };

  React.useEffect(() => {
    if (open) {
      emitGTM("open");
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        disableEscapeKeyDown
        disableBackdropClose
        PaperProps={{
          style: {
            padding: 100,
          },
        }}
      >
        <DialogTitle
          onClose={() => {
            onClose();
            emitGTM("close");
          }}
        >
          Bulk Add Customers
        </DialogTitle>
        <DialogContent>
          <Importer
            theme={importerTheme}
            fields={fields}
            maxFileSize={10 * 1000 * 1000}
            maxUploadSize={100 * 1000 * 1000}
            onLargeFile={(file: File) => {
              setLargeFile(file);
              onClose();
              setLargeFileDialogOpen(true);
            }}
            onComplete={async (data: any) => {
              try {
                postBulkAdd(data);
                onClose();
              } catch (e) {
                throw new Error();
              }
            }}
          />
        </DialogContent>
      </Dialog>

      <LargeFileUploadDialog
        open={largeFileDialogOpen}
        onClose={() => setLargeFileDialogOpen(false)}
        audienceName={segmentDetails?.name ?? value ?? ""}
        initialFile={largeFile}
        existingSegmentId={segmentDetails?.id}
        onSuccess={(segmentId) =>
          onSuccess?.(segmentDetails ?? { id: segmentId, name: value })
        }
      />
    </>
  );
}

export default TinyImporter;
